import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  ResponsiveContainer, XAxis, YAxis, Tooltip,
  BarChart, Bar,
} from 'recharts';
import CandlestickChart from './CandlestickChart';
import IndexChart from './IndexChart';
import EarningsPanel from './EarningsPanel';
import NewsPanel from './NewsPanel';

// ─── Pure helpers ────────────────────────────────────────────────────────────

function parseCSV(text) {
  const lines = text.trim().split('\n');
  const headers = lines[0].split(',').map(h => h.trim());
  return lines.slice(1).map(line => {
    const values = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { inQuotes = !inQuotes; }
      else if (ch === ',' && !inQuotes) { values.push(current); current = ''; }
      else { current += ch; }
    }
    values.push(current);
    return Object.fromEntries(headers.map((h, i) => [h, (values[i] || '').trim()]));
  });
}

function deduplicateHoldings(rows) {
  const seen = new Map();
  const sorted = [...rows].sort((a, b) => (b.amended === 'yes' ? 1 : 0) - (a.amended === 'yes' ? 1 : 0));
  for (const row of sorted) {
    const key = `${row.period}_${row.cusip}`;
    if (!seen.has(key)) seen.set(key, row);
  }
  return Array.from(seen.values());
}

function formatQuarter(iso) {
  if (!iso) return '';
  const [year, month] = iso.split('-');
  const q = Math.ceil(parseInt(month) / 3);
  return `Q${q} ${year}`;
}

function formatValue(thousands) {
  if (thousands >= 1000000) return `$${(thousands / 1000000).toFixed(1)}B`;
  return `$${(thousands / 1000).toFixed(0)}M`;
}

function normalize(rows) {
  return rows.map(r => ({
    ...r,
    value_thousands: parseInt(r.value_thousands) || 0,
    shares_prn_amt: r.shares_prn_amt ? (parseInt(r.shares_prn_amt) || null) : null,
  }));
}

// ─── Sub-components ──────────────────────────────────────────────────────────

const ChartSection = ({ title, children, controls }) => (
  <div style={{ background: '#161b22', border: '1px solid #21262d', borderRadius: 8, padding: 16, marginBottom: 20 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
      <span style={{ fontSize: 11, color: '#8b949e', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</span>
      {controls && <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>{controls}</div>}
    </div>
    {children}
  </div>
);

const selectStyle = {
  background: '#0d1117', border: '1px solid #21262d', color: '#e6edf3',
  padding: '5px 10px', fontSize: 11, borderRadius: 4, fontFamily: 'monospace', cursor: 'pointer',
};

// Custom Tooltip
const PortfolioTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{ background: '#21262d', border: '1px solid #30363d', borderRadius: 6, padding: '8px 12px', fontSize: 11 }}>
      <div style={{ color: '#58a6ff', fontWeight: 700, marginBottom: 4 }}>{d.quarter}</div>
      <div style={{ color: '#e6edf3' }}>Total Value: {formatValue(d.totalValue)}</div>
      <div style={{ color: '#8b949e' }}>Holdings: {d.holdingCount}</div>
    </div>
  );
};


// ─── Main Dashboard ───────────────────────────────────────────────────────────

export default function Holdings13FDashboard() {
  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedQuarter, setSelectedQuarter] = useState('');
  const [selectedStock, setSelectedStock] = useState('');
  const [resolvedTicker, setResolvedTicker] = useState(null);
  const [sortConfig, setSortConfig] = useState({ col: 'rank', dir: 'asc' });

  const priceChartRef = useRef(null);

  // ── Data loading ────────────────────────────────────────────────────────
  useEffect(() => {
    fetch('/form13f_holdings.csv')
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.text(); })
      .then(text => {
        const rows = normalize(deduplicateHoldings(parseCSV(text)));
        setHoldings(rows);
        const periods = [...new Set(rows.map(r => r.period))].sort();
        setSelectedQuarter(periods[periods.length - 1]);
        setLoading(false);
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  // ── Computed data ────────────────────────────────────────────────────────
  const periods = useMemo(() => [...new Set(holdings.map(r => r.period))].sort(), [holdings]);

  const portfolioTotalByPeriod = useMemo(() => {
    const map = new Map();
    for (const p of periods) {
      const total = holdings.filter(r => r.period === p).reduce((s, r) => s + r.value_thousands, 0);
      map.set(p, total);
    }
    return map;
  }, [holdings, periods]);

  const portfolioHistory = useMemo(() =>
    periods.map(p => ({
      period: p,
      quarter: formatQuarter(p),
      totalValue: portfolioTotalByPeriod.get(p),
      holdingCount: holdings.filter(r => r.period === p).length,
    })),
    [periods, portfolioTotalByPeriod, holdings]
  );

  const quarterHoldings = useMemo(() =>
    holdings.filter(r => r.period === selectedQuarter),
    [holdings, selectedQuarter]
  );

  const quarterTotal = useMemo(() =>
    quarterHoldings.reduce((s, r) => s + r.value_thousands, 0),
    [quarterHoldings]
  );

  const tableData = useMemo(() => {
    const priorIdx = periods.indexOf(selectedQuarter) - 1;
    const priorPeriod = priorIdx >= 0 ? periods[priorIdx] : null;
    const priorMap = new Map(
      (priorPeriod ? holdings.filter(r => r.period === priorPeriod) : []).map(r => [r.cusip, r])
    );
    const total = quarterTotal;
    return [...quarterHoldings]
      .sort((a, b) => b.value_thousands - a.value_thousands)
      .map((r, i) => {
        const prior = priorMap.get(r.cusip);
        let changeLabel = '—', changePct = null;
        if (!prior) {
          changeLabel = 'NEW';
        } else if (prior.value_thousands > 0) {
          changePct = +((r.value_thousands - prior.value_thousands) / prior.value_thousands * 100).toFixed(1);
          changeLabel = changePct > 0 ? `+${changePct}%` : `${changePct}%`;
        }
        return {
          rank: i + 1,
          name: r.name_of_issuer,
          titleOfClass: r.title_of_class,
          value_thousands: r.value_thousands,
          valueM: +(r.value_thousands / 1000).toFixed(1),
          shares: r.shares_prn_amt,
          pct: total > 0 ? +((r.value_thousands / total) * 100).toFixed(2) : 0,
          changeLabel,
          changePct,
          isNew: !prior,
          cusip: r.cusip,
        };
      });
  }, [holdings, selectedQuarter, periods, quarterHoldings, quarterTotal]);

  const sortedTableData = useMemo(() => {
    const colMap = { rank: 'rank', valueM: 'valueM', pct: 'pct', changePct: 'changePct' };
    const key = colMap[sortConfig.col] || 'rank';
    return [...tableData].sort((a, b) => {
      const av = a[key] ?? (sortConfig.dir === 'asc' ? Infinity : -Infinity);
      const bv = b[key] ?? (sortConfig.dir === 'asc' ? Infinity : -Infinity);
      return sortConfig.dir === 'asc' ? av - bv : bv - av;
    });
  }, [tableData, sortConfig]);

  const handleSort = (col) => {
    setSortConfig(prev => ({
      col,
      dir: prev.col === col && prev.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleRowClick = (name) => {
    setSelectedStock(name);
    setTimeout(() => priceChartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  // ── Loading / Error states ───────────────────────────────────────────────
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, gap: 12 }}>
      <div style={{ width: 28, height: 28, border: '3px solid #21262d', borderTop: '3px solid #58a6ff', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      <span style={{ color: '#8b949e', fontSize: 12 }}>Loading 13F holdings data...</span>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (error) return (
    <div style={{ padding: 40, color: '#FF6B6B', fontSize: 12 }}>
      Failed to load data: {error}. Make sure form13f_holdings.csv is in the public/ folder.
    </div>
  );

  // ── Y-axis formatter for portfolio chart ─────────────────────────────────
  const yAxisFormatter = (v) => v >= 1000000 ? `$${(v / 1000000).toFixed(0)}B` : `$${(v / 1000).toFixed(0)}M`;

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '20px 24px' }}>

      {/* ── Chart 1: Candlestick Price Chart ── */}
      <div ref={priceChartRef}>
        <CandlestickChart
          companyName={selectedStock}
          centerDate={selectedQuarter}
          onTickerResolved={setResolvedTicker}
        />
      </div>

      {/* ── Charts 2 + 3 side by side ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 20, marginBottom: 0 }}>

        {/* Chart 2: Earnings Panel */}
        <ChartSection title={selectedStock ? `Earnings — ${selectedStock}` : 'Earnings'}>
          {selectedStock ? (
            <EarningsPanel
              ticker={resolvedTicker}
              companyName={selectedStock}
              centerDate={selectedQuarter}
            />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: '#8b949e', fontSize: 11 }}>
              Click a holding to view earnings
            </div>
          )}
        </ChartSection>

        {/* Chart 3: Index Chart */}
        <IndexChart centerDate={selectedQuarter} />
      </div>

      {/* ── Chart 4: News & Analytics Panel ── */}
      <ChartSection
        title={selectedStock
          ? `News & Analysis — ${selectedStock} · ${formatQuarter(selectedQuarter)}`
          : 'News & Analysis'}
      >
        <NewsPanel
          ticker={resolvedTicker}
          companyName={selectedStock}
          centerDate={selectedQuarter}
        />
      </ChartSection>

      {/* ── Chart 5: Holdings Table ── */}
      <ChartSection
        title={`Holdings — ${formatQuarter(selectedQuarter)} (${tableData.length} positions)`}
        controls={
          <select style={selectStyle} value={selectedQuarter} onChange={e => setSelectedQuarter(e.target.value)}>
            {[...periods].reverse().map(p => (
              <option key={p} value={p}>{formatQuarter(p)}</option>
            ))}
          </select>
        }
      >
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #21262d' }}>
                {[
                  { label: '#', col: 'rank' },
                  { label: 'Company', col: null },
                  { label: 'Class', col: null },
                  { label: 'Value ($M)', col: 'valueM' },
                  { label: 'Shares', col: null },
                  { label: '% Portfolio', col: 'pct' },
                  { label: 'QoQ Change', col: 'changePct' },
                ].map(({ label, col }) => (
                  <th
                    key={label}
                    onClick={col ? () => handleSort(col) : undefined}
                    style={{
                      padding: '8px 12px', color: '#8b949e', textAlign: 'left',
                      cursor: col ? 'pointer' : 'default', userSelect: 'none', fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {label}
                    {col && sortConfig.col === col && (
                      <span style={{ marginLeft: 4 }}>{sortConfig.dir === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedTableData.map(row => (
                <tr
                  key={row.cusip}
                  onClick={() => handleRowClick(row.name)}
                  style={{ borderBottom: '1px solid #21262d', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(88,166,255,0.05)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ padding: '7px 12px', color: '#8b949e' }}>{row.rank}</td>
                  <td style={{ padding: '7px 12px', color: '#e6edf3', fontWeight: 600 }}>{row.name}</td>
                  <td style={{ padding: '7px 12px', color: '#8b949e' }}>{row.titleOfClass}</td>
                  <td style={{ padding: '7px 12px', color: '#e6edf3' }}>${row.valueM}M</td>
                  <td style={{ padding: '7px 12px', color: '#8b949e' }}>
                    {row.shares != null ? row.shares.toLocaleString() : '—'}
                  </td>
                  <td style={{ padding: '7px 12px', color: '#e6edf3' }}>{row.pct}%</td>
                  <td style={{
                    padding: '7px 12px', fontWeight: 600,
                    color: row.isNew ? '#FFEAA7' : row.changePct > 0 ? '#00D4AA' : row.changePct < 0 ? '#FF6B6B' : '#8b949e',
                  }}>
                    {row.changeLabel}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartSection>

    </div>
  );
}
